import { Box, Button, MenuItem, Popover, TextField, Typography } from "@mui/material";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useState } from "react";
import { validationNegativeValue } from "../../../../../utils/validationUtils";

interface FilterRangeUsageStorageProps {
    disabled?: boolean;
    minUsage: number | undefined;
    maxUsage: number | undefined;
    setMinUsage: Dispatch<SetStateAction<number | undefined>>;
    setMaxUsage: Dispatch<SetStateAction<number | undefined>>;
}

enum UnitStorage {
    KB = "KB",
    MB = "MB",
    GB = "GB",
    TB = "TB"
}

export const FilterRangeUsageStorage: React.FC<FilterRangeUsageStorageProps> = ({ disabled, minUsage, maxUsage, setMinUsage, setMaxUsage }) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [minUsageInternal, setMinUsageInternal] = useState<number | undefined>();
    const [maxUsageInternal, setMaxUsageInternal] = useState<number | undefined>();
    const [unitMinUsage, setUnitMinUsage] = useState<UnitStorage>(UnitStorage.GB);
    const [unitMaxUsage, setUnitMaxUsage] = useState<UnitStorage>(UnitStorage.GB);
    const [errorMinUsage, setErrorMinUsage] = useState<string|undefined>(undefined);
    const [errorMaxUsage, setErrorMaxUsage] = useState<string|undefined>(undefined);
    const open = Boolean(anchorEl);
    const id = open ? "filter-popover" : undefined;

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClearFilter = () => {
        setMinUsage(undefined);
        setMaxUsage(undefined);
        setMinUsageInternal(undefined);
        setMaxUsageInternal(undefined);
        setErrorMinUsage(undefined);
        setErrorMaxUsage(undefined);
        handleClose();
    };

    const handleApplyFilter = () => {
        var hasError = false;
        if (minUsageInternal !== undefined) {
            const errorMessage = validationNegativeValue(minUsageInternal, t);
            if (errorMessage) {
                hasError = true;
            }
            setErrorMinUsage(errorMessage);
        }
        if (maxUsageInternal !== undefined) {
            const errorMessage = validationNegativeValue(maxUsageInternal, t);
            if (errorMessage) {
                hasError = true;
            }
            setErrorMaxUsage(errorMessage);
        }

        if (!hasError) {
            if (minUsageInternal !== undefined)
                setMinUsage(convertToBytes(minUsageInternal, unitMinUsage));
            if (maxUsageInternal !== undefined)
                setMaxUsage(convertToBytes(maxUsageInternal, unitMaxUsage));
            
            setErrorMinUsage(undefined);
            setErrorMaxUsage(undefined);
            handleClose();
        }
    };

    const changeMinUsage = (value: number | undefined) => {
        if (value === undefined) {
            setMinUsageInternal(undefined);
        } else {
            setMinUsageInternal(value);
        }
    };
    const changeMaxUsage = (value: number | undefined) => {
        if (value === undefined) {
            setMaxUsageInternal(undefined);
        } else {
            setMaxUsageInternal(value);
        }
    };

    function convertToBytes(value: number, unit: UnitStorage): number {
        console.log({ value, unit });
        switch (unit) {
            case UnitStorage.KB:
                return value * 1024;
            case UnitStorage.MB:
                return value * 1024 ** 2;
            case UnitStorage.GB:
                return value * 1024 ** 3;
            case UnitStorage.TB:
                return value * 1024 ** 4;
            default:
                throw new Error(`Unidade de medida inválida: ${unit}`);
        }
    }

    return (
        <>
            <Button
                sx={{
                    border: "none",
                    color: '#22638D',
                    textTransform: 'none',
                    font: 'normal normal normal 14px/21px Segoe UI',
                    "&:hover": {
                        border: "none",
                        background: '#22638D',
                        color: '#E9EFF4'
                    }
                }}
                disabled={disabled}
                variant="outlined"
                startIcon={<FilterAltIcon />}
                onClick={handleOpen}
            >
                {t("FilterRangeUsageStorage.buttonFilter")}
            </Button>
            {/* Popover de Filtro */}
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: 260,
                        borderRadius: "8px",
                        boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                    },
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t("FilterRangeUsageStorage.title") || "Filtrar por uso (MB)"}
                </Typography>

                <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" flexDirection="row" >
                        <TextField
                            sx={{ flex: 2, borderRight: "none", borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            label={t("FilterRangeUsageStorage.greaterThan")}
                            type="number"
                            inputProps={{min: 0}}
                            value={maxUsageInternal ?? ""}
                            onChange={(e) => changeMaxUsage(e.target.value ? parseFloat(e.target.value) : undefined)}
                            size="small"
                            error={errorMaxUsage !== undefined}
                            helperText={errorMaxUsage}
                        />
                        <TextField
                            sx={{ flex: 1, borderLeft: "none", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            id="outlined-select-currency"
                            select
                            defaultValue={UnitStorage.GB}
                            size="small"
                            onChange={(e) => {setUnitMaxUsage(e.target.value as UnitStorage)}}
                        >
                            {Object.entries(UnitStorage).map(([key, value]) => (
                                <MenuItem key={key} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box display="flex" flexDirection="row" >
                        <TextField
                            sx={{ flex: 2, borderRight: "none", borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            label={t("FilterRangeUsageStorage.lessThan")}
                            type="number"
                            inputProps={{min: 0}}
                            value={minUsageInternal ?? ""}
                            onChange={(e) => changeMinUsage(e.target.value ? parseFloat(e.target.value) : undefined)}
                            size="small"
                            error={errorMinUsage !== undefined}
                            helperText={errorMinUsage}
                        />
                        <TextField
                            sx={{ flex: 1, borderLeft: "none", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            id="outlined-select-currency"
                            select
                            defaultValue={UnitStorage.GB}
                            size="small"
                            onChange={(e) => {setUnitMinUsage(e.target.value as UnitStorage)}}
                        >
                            {Object.entries(UnitStorage).map(([key, value]) => (
                                <MenuItem key={key} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Box display="flex" justifyContent="space-between" mt={1}>
                        <Button 
                            size="small" 
                            onClick={handleClearFilter} 
                            sx={{ color: "#22638D" }}    
                        >
                            {t("FilterRangeUsageStorage.clear")}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleApplyFilter}
                            sx={{ backgroundColor: "#22638D", "&:hover": { backgroundColor: "#1a5478" } }}
                        >
                            {t("FilterRangeUsageStorage.apply")}
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}